/**
 * @dependencies: https://github.com/videojs/video.js
 *
 * Creates a video player based on Brightcove's videojs library
 */
import videojs from 'video.js';
import 'videojs-youtube';
import scrollMonitor from 'scrollmonitor';
import { objectAssign } from '../helpers/utilities';
import modules from '../helpers/modules';
import { isMobileDevice } from '../helpers/mobile';

window.VIDEOJS_NO_DYNAMIC_STYLE = false;

const
    supportedSources = {
        FILE: 'file',
        YOUTUBE: 'youtube',
        BRIGHTCOVE: 'brightcove',
        VIMEO: 'vimeo'
    },
    defaultSettings = {
        'settings': {
            'controls': true,
            'autoplay': false,
            'preload': 'auto'
        },
        'sources': [
            { type: 'video/mp4', src: 'http://clips.vorwaerts-gmbh.de/big_buck_bunny.mp4' },
            { type: 'video/webm', src: 'http://clips.vorwaerts-gmbh.de/big_buck_bunny.webm' },
        ]
    },
    defaultTemplates = {
        'autoplay-no-controls-mute': {
            simplePlayback: false,
            controls: false,
            controlBar: false,
            bigPlayButton: false,
            playToggle: true,
            loop: true,
            autoplay: true,
            preload: 'auto',
            playsinline: true,
            muted: true,
            youtube: {
                playsinline: 1
            }
        },
        'simple-controls': {
            controls: true,
            bigPlayButton: false,
            playToggle: true,
            loop: false,
            autoplay: false,
            preload: 'auto',
            playsinline: true,
            youtube: {
                ytControls: 1
            }
        }
    },
    TRACKING_LABELS = {
        play: 'Start',
        pause: 'Pause',
        update: 'Time Update'
    },
    PLAYBACK_MILESTONES = [ 100, 75, 50, 25, 0 ],
    DEVICE_TYPE = isMobileDevice() ? 'mobile' : 'desktop';

// it's generally a good idea to remove almost all UI and start from scratch
export default class Video {
    constructor( query ) {
        const videos = document.querySelectorAll(query);

        [].forEach.call( videos, ( video ) => {
            const playerData = JSON.parse( video.getAttribute( 'data-video' ) ),
                optionsIsString = typeof playerData['options'] === 'string',
                overrideOptions = optionsIsString ? {} : playerData['options'],
                templateName = optionsIsString ? playerData['options'] : overrideOptions.template,
                matchedTemplate = templateName && defaultTemplates[ templateName ] ? defaultTemplates[ templateName ] : {};

            video.templateOptions = objectAssign({}, matchedTemplate, overrideOptions);

            /**
             * Add sources to videojs based on the sources array.
             */
            if ( playerData['sources'][DEVICE_TYPE].length && Array.isArray( playerData['sources'][DEVICE_TYPE] )) {
                let hasYouTube = false;

                // To avoid protocol issues, update all "src" keys with current protocol
                Object.keys(playerData['sources'][DEVICE_TYPE]).forEach((index) => {
                    if ( playerData['sources'][DEVICE_TYPE][index]['type'] !== 'bcid' ) {
                        playerData['sources'][DEVICE_TYPE][index]['src'] = playerData['sources'][DEVICE_TYPE][index]['src'].replace(/https?:/, window.location.protocol);
                    }

                    hasYouTube = playerData['sources'][DEVICE_TYPE][index]['type'].indexOf(supportedSources.YOUTUBE) !== -1 || hasYouTube;
                });

                //If one of the sources is youtube, update the templateOptions accordingly.
                if (hasYouTube) {
                    video.templateOptions.youtube = video.templateOptions.youtube || {};
                    video.templateOptions.youtube.origin = window.location.hostname;

                    // video.templateOptions.controls = false;
                    video.templateOptions.controlBar = false;
                }

                if ( playerData['sources'][DEVICE_TYPE].filter(source => source.type == 'bcid').length >= 1 ) {
                    this.addRemoteSourcesByBCID(video, playerData['sources'][DEVICE_TYPE].filter(source => source.type == 'bcid')[0].src ).then(() => {
                        this.initVideoOnView(video);
                    });
                } else {
                    this.addVideoSources(video, playerData['sources'][DEVICE_TYPE] ).then(() => {
                        this.initVideoOnView(video);
                    });
                }
            } else {
                this.addVideoSources( video ).then(() => {
                    this.initVideoOnView(video);
                });
            }

            video.removeAttribute( 'data-video' );
        });
    }

    videoJSInit( video ) {
        let elementWatcher;

        /*
         * Assign videojs instance to player variable
         */

        video.player = videojs( video, video.templateOptions);
        video.playerElement = video.player.el();

        elementWatcher = scrollMonitor.create( video.playerElement );

        _initSources(video);

        //Check if site is in Page Designer edit mode, do not auto play if that's the case.
        //Helps with performance when editing a page.
        if (!window.SFRA.SitePreferences.pdEditMode) {
            elementWatcher.enterViewport(this.checkForAutoplay.bind(this, video));

            elementWatcher.exitViewport(this.deferAutoplay.bind(this, video));
        }

        /**
         * Play Pause Button Setup.
         */
        this.interfaceSetup( video );

        function _initSources(video) {
            video.player.src(video.videoSources);
        }
    }


    /**
     *
     * @param {videojs} player
     * @param {*} playerElement
     */
    interfaceSetup( video ) {
        let self = this,
            playerElement = video.player.el() || video,
            containerParent = playerElement.closest('[data-video-component="parent"]') || playerElement.parentNode,
            isAutoPlay = video.templateOptions.autoplay || video.templateOptions.deferAutoplay,
            playToggle = video.player.getChild('PlayToggle');

        containerParent.classList.add(`set--video-${isAutoPlay ? 'autoplay' : 'manual'}`);
        containerParent.classList.add(`set--video-${video.player.controls() ? 'has-controls' : 'no-controls'}`);
        containerParent.classList.add(`set--video-overlay-${video.templateOptions.overlayOptions || 'show-permanent'}`);

        if (playToggle) {
            playToggle.setAttribute('class', 'vjs-play-control vjs-play-toggle');
        }

        video.player.on('ready', function() {
            containerParent.classList.add('set--video-ready');
        });

        // bind events
        video.player.on('pause', function() {
            if (containerParent) {
                containerParent.classList.add('set--video-paused');
                containerParent.classList.remove('set--video-playing');
            }

            self.handleTracking(this, TRACKING_LABELS.pause);
        });

        video.player.on('play', function() {
            scrollMonitor.recalculateLocations();

            if (containerParent) {
                containerParent.classList.add('set--video-played');
                containerParent.classList.add('set--video-playing');
                containerParent.classList.remove('set--video-paused');
            }

            self.handleTracking(this, TRACKING_LABELS.play);
        });

        //Sets up milestone tracking for videos.
        //Milestones are set at 25, 50, 75.
        video.player.on('timeupdate', function() {
            this.previousMilestone = self.handleTracking(this, TRACKING_LABELS.update, true);
        });

        video.player.on('ended', function() {
            self.handleTracking(this, TRACKING_LABELS.pause);
        });

        let muteButton = containerParent.querySelector('.mute-button');

        if (muteButton) {
            if (video.muted || playerElement.children[0].muted) {
                $(muteButton).addClass('muted');
                video.muted = true;
            } else {
                if ($(muteButton).hasClass('muted')) {
                    $(muteButton).removeClass('muted');
                    video.muted = false;
                }
            }

            $(muteButton).on('click', function() {
                video.muted = !video.muted;
                playerElement.children[0].muted = video.muted;
                if (video.muted && playerElement.children[0].muted) {
                    $(this).addClass('muted');
                    video.volume = 0;
                } else {
                    video.volume = 1;
                    if ($(this).hasClass('muted')) {
                        $(this).removeClass('muted');
                    }
                }
            });
        }
    }

    _getTrackPercentage(video) {
        return (video.currentTime() / video.duration()) * 100;
    }

    _getCurrentMilestone(video) {
        let trackPercentage = this._getTrackPercentage(video);

        if (isNaN(trackPercentage) || trackPercentage > 100) {
            return 0;
        }

        for (let i = 0; i <= PLAYBACK_MILESTONES.length; i++) {
            if (trackPercentage >= PLAYBACK_MILESTONES[i]) {
                return PLAYBACK_MILESTONES[i];
            }
        }
    }

    handleTracking(video, action, atMilestones) {
        //Do not track if it is a Youtube video, instead use YT's tracking capabilities.
        if (video.techName_ !== 'Youtube') {
            let currentMilestone = this._getCurrentMilestone(video);

            if (atMilestones && (currentMilestone === video.previousMilestone)) {
                return currentMilestone;
            }

            modules.tracking.init(null, {
                trackEvent: 'videotracking',
                trackObject: {
                    videoIntercation: action,
                    videoStatus: `Played ${currentMilestone}%`,
                    videoName: video.id_
                }
            });

            return currentMilestone;
        }
    }

    /**
     *
     * @param {videojs} player
     * @param {string} [videoSources=defaultSettings['sources']]
     * @memberof Video
     */
    addVideoSources( videoPlayer, videoSources = defaultSettings['sources'] ) {
        return new Promise((resolve) => {
            videoPlayer.videoSources = videoSources;
            resolve();
        })
    }


    addRemoteSourcesByBCID( videoPlayer, bcid ) {
        return new Promise((resolve) => {
            // hard assumption that BCIDs are all numerical
            if ( Number.isInteger(parseInt(bcid)) ) {
                $.ajax({
                    method: 'GET',
                    url: `https://edge.api.brightcove.com/playback/v1/accounts/${window.Constants.brightcove.BC_ACCOUNT_ID}/videos/${bcid}`,
                    headers: {
                        'Content-Type': 'application/json',
                        'BCOV-Policy': window.Constants.brightcove.BC_POLICY_ID
                    }
                }).done((data) => {
                    Object.keys(data['sources']).forEach((val) => {
                        if ( data['sources'][val]['type'] !== 'bcid' ) {
                            data['sources'][val]['src'] = data['sources'][val]['src'].replace(/https?:/, window.location.protocol);
                        }
                    });
                    this.addVideoSources(videoPlayer, data['sources'].filter(singleSource => singleSource.type == 'application/x-mpegURL') ).then(() => {
                        resolve();
                    });
                });
            } else {
                resolve();
            }
        })
    }

    checkForAutoplay(video) {
        if (video.templateOptions.deferAutoplay || video.templateOptions.autoplay || video.templateOptions.continuePlaying) {
            video.player.play();
        }
    }

    deferAutoplay(video) {
        video.player.pause();
    }

    initVideoOnView(video) {
        let elementWatcher = scrollMonitor.create( video ),
            context = this;

        elementWatcher.enterViewport( function() {
            context.videoJSInit( video );
            elementWatcher.destroy();
        });
    }
}
